/**
 * Encounter List Component Types
 *
 * Unified type definitions for the reusable EncounterList component
 * that handles both standalone (Appointments) and connected (Encounters) modes.
 */

import { Appointment } from '@/types/appointment';
import { Encounter } from '@/types/encounter';

// ============================================================================
// Unified Data Model
// ============================================================================

export type RecordingStatus = 'pending' | 'recording' | 'completed' | 'failed';
export type TranscriptionStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type EncounterStatus =
  | 'scheduled'
  | 'in-progress'
  | 'completed'
  | 'cancelled'
  | 'no-show'
  | 'pending'
  | 'recording'
  | 'failed'
  | 'sent-to-ehr'
  | 'failed-to-send';
export type MatchStatus = 'matched' | 'unmatched' | 'pending';
export type AppointmentType = 'consultation' | 'follow-up' | 'procedure' | 'emergency' | 'other';
export type OperationMode = 'standalone' | 'connected';

/**
 * Unified encounter item that normalizes both Appointment and Encounter data
 */
export interface UnifiedEncounterItem {
  id: string;
  patientName: string;
  patientMrn?: string;
  patientId?: string;
  scheduledDate: Date;
  type: string;
  location?: string;
  chiefComplaint?: string;
  providerName?: string;
  providerId?: string;
  status: EncounterStatus;
  recordingStatus?: RecordingStatus;
  transcriptionStatus?: TranscriptionStatus;
  matchStatus?: MatchStatus;
  externalSystem?: string;
  externalId?: string;
  hasTranscript: boolean;
  canStartRecording: boolean;
  isTranscribing?: boolean;
  _raw: Appointment | Encounter;
  _type: 'appointment' | 'encounter';
}

// ============================================================================
// Filter Types
// ============================================================================

export interface EncounterListFilters {
  search?: string;
  status?: string[];
  type?: string[];
  recordingStatus?: string[];
  transcriptionStatus?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  providerId?: string;
  patientId?: string;
  matchStatus?: string[];
  externalSystemId?: string;
}

export interface FilterOption {
  value: string;
  label: string;
  color?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

export interface FilterDimension {
  key: keyof EncounterListFilters;
  label: string;
  type: 'select' | 'multi-select' | 'date-range' | 'autocomplete';
  options?: FilterOption[];
  placeholder?: string;
  modes?: OperationMode[];
}

export interface ActiveFilter {
  key: keyof EncounterListFilters;
  label: string;
  displayValue: string;
  value: unknown;
}

// ============================================================================
// Pagination Types
// ============================================================================

export interface PaginationState {
  page: number;
  limit: number;
  total: number;
}

export interface PaginationConfig {
  defaultPage?: number;
  defaultLimit?: number;
  pageSizeOptions?: number[];
}

// ============================================================================
// Component Configuration
// ============================================================================

export interface EncounterListConfig {
  mode: OperationMode;
  variant?: 'full' | 'compact';
  title?: string;
  subtitle?: string;
  enableSearch?: boolean;
  enableFilters?: boolean;
  enablePagination?: boolean;
  enableInfiniteScroll?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  canCreate?: boolean;
  showMatchStatus?: boolean;
  showExternalSystem?: boolean;
  onItemClick?: (item: UnifiedEncounterItem) => void;
  onStartRecording?: (item: UnifiedEncounterItem) => void;
  onViewTranscript?: (item: UnifiedEncounterItem) => void;
  onViewDetails?: (item: UnifiedEncounterItem) => void;
  /**
   * Offer "Change provider" on each appointment (standalone admins, AMP-137). Called with the item
   * and a function that reloads the list once the provider has changed.
   */
  onChangeProvider?: (item: UnifiedEncounterItem, onChanged: () => void) => void;
  onCreateClick?: () => void;
}

// ============================================================================
// Context State
// ============================================================================

export interface EncounterListState {
  items: UnifiedEncounterItem[];
  filters: EncounterListFilters;
  pagination: PaginationState;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
}

export interface EncounterListActions {
  setFilters: (filters: Partial<EncounterListFilters>) => void;
  clearFilters: () => void;
  removeFilter: (key: keyof EncounterListFilters) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  loadMore: () => void;
  refresh: () => void;
}

export interface EncounterListContextValue extends EncounterListState, EncounterListActions {
  config: EncounterListConfig;
  isMobile: boolean;
  activeFilters: ActiveFilter[];
}

// ============================================================================
// API Types
// ============================================================================

export interface AppointmentFiltersAPI {
  page?: number;
  limit?: number;
  status?: string;
  providerId?: string;
  patientId?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  type?: string;
  recordingStatus?: string;
  transcriptionStatus?: string;
}

export interface EncounterFiltersAPI {
  page?: number;
  limit?: number;
  matchStatus?: string;
  dateFrom?: string;
  dateTo?: string;
  assignedUserId?: string;
  search?: string;
  type?: string;
  status?: string;
  externalSystemId?: string;
}

export interface ListResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// ============================================================================
// Filter Constants
// ============================================================================

/**
 * Status filter of the connected encounter list: the status the list shows, which the backend
 * computes from the detail status and the EHR delivery outcome, filtered on the server.
 */
export const STATUS_OPTIONS: FilterOption[] = [
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-100 text-blue-800' },
  { value: 'in-progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'sent-to-ehr', label: 'Sent to EHR', color: 'bg-green-100 text-green-800' },
  { value: 'failed-to-send', label: 'Failed to Send', color: 'bg-red-100 text-red-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

/**
 * Status filter of the standalone appointment list: the statuses the list shows
 * (appointmentAdapter's mapToEncounterStatus), filtered on the server (AMP-140).
 */
export const STANDALONE_STATUS_OPTIONS: FilterOption[] = [
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-100 text-blue-800' },
  { value: 'recording', label: 'Recording', color: 'bg-purple-100 text-purple-800' },
  { value: 'in-progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'failed', label: 'Failed', color: 'bg-red-100 text-red-800' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-gray-100 text-gray-700' },
  { value: 'no-show', label: 'No-show', color: 'bg-orange-100 text-orange-800' },
];

export interface EncounterStatusStyle {
  label: string;
  /** Dot classes; a border-only dot reads as hollow. */
  dot: string;
  text: string;
  bg: string;
  pulse?: boolean;
  /** Shown in place of the dot, so statuses that share a color never rely on color alone. */
  icon?: 'sent' | 'alert';
}

/**
 * Badge label and colors per status, shared by the list table and the encounter detail panel.
 * The connected statuses come in pairs per color (blue, green, red); in each pair the one that
 * matters more gets the stronger badge, and the lighter one a hollow dot.
 */
export const ENCOUNTER_STATUS_STYLES: Record<string, EncounterStatusStyle> = {
  scheduled: { label: 'Scheduled', dot: 'border-2 border-blue-400', text: 'text-blue-600', bg: 'bg-blue-50' },
  'in-progress': { label: 'In Progress', dot: 'bg-blue-600', text: 'text-blue-800', bg: 'bg-blue-100', pulse: true },
  transcribing: { label: 'Transcribing', dot: 'bg-blue-600', text: 'text-blue-800', bg: 'bg-blue-100', pulse: true },
  completed: { label: 'Completed', dot: 'border-2 border-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  'sent-to-ehr': { label: 'Sent to EHR', dot: 'bg-emerald-600', text: 'text-emerald-800', bg: 'bg-emerald-100', icon: 'sent' },
  'failed-to-send': { label: 'Failed to Send', dot: 'bg-red-600', text: 'text-red-800', bg: 'bg-red-100', icon: 'alert' },
  cancelled: { label: 'Cancelled', dot: 'border-2 border-red-400', text: 'text-red-600', bg: 'bg-red-50' },
  // Standalone (appointment) statuses
  ready: { label: 'Ready', dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50' },
  recording: { label: 'Recording', dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', pulse: true },
  processing: { label: 'Processing', dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', pulse: true },
  failed: { label: 'Failed', dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50' },
  'no-show': { label: 'No-show', dot: 'bg-orange-400', text: 'text-orange-700', bg: 'bg-orange-50' },
  pending: { label: 'Pending', dot: 'bg-gray-400', text: 'text-gray-600', bg: 'bg-gray-100' },
};

export const TYPE_OPTIONS: FilterOption[] = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow-up', label: 'Follow-up' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'other', label: 'Other' },
];

export const RECORDING_STATUS_OPTIONS: FilterOption[] = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-800' },
  { value: 'recording', label: 'Recording', color: 'bg-blue-100 text-blue-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'failed', label: 'Failed', color: 'bg-red-100 text-red-800' },
];

export const TRANSCRIPTION_STATUS_OPTIONS: FilterOption[] = [
  { value: 'pending', label: 'Pending', color: 'bg-gray-100 text-gray-800' },
  { value: 'processing', label: 'Processing', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' },
  { value: 'failed', label: 'Failed', color: 'bg-red-100 text-red-800' },
];

export const MATCH_STATUS_OPTIONS: FilterOption[] = [
  { value: 'matched', label: 'Matched', color: 'bg-green-100 text-green-800' },
  { value: 'unmatched', label: 'Unmatched', color: 'bg-red-100 text-red-800' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
];

// NOTE: A hardcoded EXTERNAL_SYSTEM_OPTIONS list was removed here. It was an
// unused export. The live source of truth for EHR system options is the
// useEhrRegistry() hook (useEhrRegistry().options), which must be consumed
// from within a component/hook body, never at module scope.

export const DATE_PRESET_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'this-week', label: 'This Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-7-days', label: 'Last 7 Days' },
  { value: 'last-30-days', label: 'Last 30 Days' },
  { value: 'custom', label: 'Custom Range' },
];

export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
export const DEFAULT_PAGE_SIZE = 20;
